import { useMemo } from "react";
import BottomSheet from "../app/BottomSheet";

type Who = "konrad" | "ida";

const META: Record<Who, { name: string; desc: string; img: string; quote: { hi: string; rest: string } }> = {
  konrad: {
    name: "Konrad",
    desc: "Bedächtige Stimme · stellt offene Fragen",
    img: "/images/konrad.jpg",
    quote: {
      hi: "„Erzählen Sie mir vom ersten Tanz, Frau Wagner.“",
      rest: "Wie war das damals, wenn man jemanden zum ersten Mal traf?",
    },
  },
  ida: {
    name: "Ida",
    desc: "Warme Stimme · entdeckt kleine Details",
    img: "/images/ida.jpg",
    quote: {
      hi: "„Erzählen Sie mir vom ersten Tanz, Frau Wagner.“",
      rest: "Mit wem haben Sie getanzt — und wie hat das Lied geheißen?",
    },
  },
};

// VoiceSheet — bottom sheet with portrait, sample phrase, fake waveform, controls.
// Audio is a visual stub. TODO(Phase C): wire to real audio.
export default function VoiceSheet({
  who,
  onSwitch,
  onClose,
  onChoose,
}: {
  who: Who;
  onSwitch: (w: Who) => void;
  onClose: () => void;
  onChoose: (w: Who) => void;
}) {
  const m = META[who];
  const other: Who = who === "konrad" ? "ida" : "konrad";
  const otherMeta = META[other];

  // 60 SVG bars, deterministic
  const bars = useMemo(
    () =>
      Array.from({ length: 60 }).map((_, i) => {
        const seed = Math.sin(i * 1.91) * Math.cos(i * 0.43);
        const h = 8 + Math.abs(seed) * 36;
        return { h, played: i / 60 < 0.35 };
      }),
    [],
  );

  return (
    <BottomSheet open onClose={onClose} ariaLabel={`Stimmprobe ${m.name}`}>
      {/* Speaker meta */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
        <div
          className="oval-frame"
          style={{ width: 64, height: 64, flexShrink: 0, background: "var(--paper-200)", overflow: "hidden" }}
        >
          <img
            src={m.img}
            alt={m.name}
            className="photo-tone"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div className="t-caps">Stimmprobe</div>
          <div className="t-script" style={{ fontSize: 28, color: "var(--oxblood)", lineHeight: 1, fontStyle: "italic" }}>
            {m.name}
          </div>
          <div
            style={{
              fontFamily: "var(--f-body)",
              fontStyle: "italic",
              fontSize: 13,
              color: "var(--ink-faint)",
              marginTop: 2,
            }}
          >
            {m.desc}
          </div>
        </div>
      </div>

      {/* Sample quote */}
      <div
        style={{
          padding: "14px 16px",
          background: "var(--paper-100)",
          border: "1px solid rgba(43,26,15,0.10)",
          borderRadius: 10,
          marginBottom: 18,
        }}
      >
        <div className="t-caps" style={{ fontSize: 9, marginBottom: 6 }}>
          {who === "ida" ? "Sie sagt gerade" : "Er sagt gerade"}
        </div>
        <p
          style={{
            fontFamily: "var(--f-display)",
            fontStyle: "italic",
            fontSize: 22,
            lineHeight: 1.35,
            color: "var(--ink)",
            margin: 0,
          }}
        >
          <span style={{ background: "rgba(168,122,58,0.22)", padding: "0 2px" }}>{m.quote.hi}</span>{" "}
          <span style={{ color: "var(--ink-soft)" }}>{m.quote.rest}</span>
        </p>
      </div>

      {/* Waveform */}
      <svg
        viewBox="0 0 240 50"
        preserveAspectRatio="none"
        aria-hidden
        style={{ display: "block", width: "100%", height: 50, marginBottom: 8 }}
      >
        {bars.map((b, i) => {
          const w = 240 / bars.length;
          const x = i * w + w * 0.18;
          const bw = w * 0.64;
          return (
            <rect
              key={i}
              x={x}
              y={(50 - b.h) / 2}
              width={bw}
              height={b.h}
              rx={1}
              fill={b.played ? "var(--oxblood)" : "rgba(43,26,15,0.18)"}
            />
          );
        })}
      </svg>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontFamily: "var(--f-ui)",
          fontSize: 11,
          color: "var(--ink-faint)",
          marginBottom: 18,
        }}
      >
        <span>00:00</span>
        <span>0:46</span>
      </div>

      {/* Play controls — visual only, not focusable */}
      <div
        aria-hidden
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 28,
          marginBottom: 22,
        }}
      >
        <button
          tabIndex={-1}
          style={{ background: "transparent", border: "none", padding: 6, cursor: "default" }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--ink-soft)" strokeWidth="1.8">
            <path d="M3 12a9 9 0 0118-1.5" />
            <path d="M21 4v6h-6" />
          </svg>
        </button>
        <button
          tabIndex={-1}
          aria-label="Abspielen"
          style={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            background: "var(--oxblood)",
            border: "none",
            boxShadow: "0 10px 24px rgba(107,32,24,0.30)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "default",
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="var(--paper-50)">
            <polygon points="7 5 20 12 7 19 7 5" />
          </svg>
        </button>
        <button
          tabIndex={-1}
          style={{ background: "transparent", border: "none", padding: 6, cursor: "default" }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="var(--ink-soft)" strokeWidth="1.8">
            <path d="M21 12a9 9 0 11-3-6.7" />
            <path d="M21 4v6h-6" />
          </svg>
        </button>
      </div>

      {/* Choose + switch row */}
      <div style={{ display: "flex", gap: 10, marginBottom: 4 }}>
        <button
          type="button"
          className="btn-primary"
          style={{ flex: 1, boxSizing: "border-box" }}
          onClick={() => onChoose(who)}
        >
          {m.name} wählen
        </button>
      </div>
      <div style={{ textAlign: "center", marginTop: 12 }}>
        <button
          type="button"
          onClick={() => onSwitch(other)}
          style={{
            background: "transparent",
            border: "none",
            padding: "6px 8px",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            fontFamily: "var(--f-ui)",
            fontSize: 13,
            fontWeight: 500,
            color: "var(--ink-soft)",
            cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          <span
            className="oval-frame"
            style={{ width: 22, height: 22, overflow: "hidden", flexShrink: 0, display: "inline-block" }}
          >
            <img
              src={otherMeta.img}
              alt=""
              className="photo-tone"
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
          </span>
          Wechseln zu {otherMeta.name}
        </button>
      </div>
    </BottomSheet>
  );
}
